// B7 — client-side preview of submission-quality gates.
// This MIRRORS the server evaluator for inline UX only. The server remains
// authoritative: the submit transition re-checks these gates and returns HTTP
// 422 with a gates[] payload if they fail. Keep the rules here in sync with the
// backend evaluator, but never rely on this as the enforcement point.

export const MIN_NARRATIVE_WORDS = 25;

const wordCount = (text) => String(text || '').trim().split(/\s+/).filter(Boolean).length;

// Duplicate detection is EXACT (trim only) — no fuzzy/normalization here so the
// preview never over-blocks relative to the server's canonical duplicate rule.
const normalizeForDuplicate = (text) => String(text || '').trim();

const hasOverride = (overrides, gateCode) =>
  (overrides || []).some(
    (override) =>
      override &&
      override.gateCode === gateCode &&
      String(override.owner || '').trim() &&
      String(override.note || '').trim()
  );

/**
 * Evaluate the submission-quality gates client-side.
 * @returns {{ status: 'ok'|'blocked', gates: Array }}
 */
export const evaluateQualityGates = ({
  narratives = [],
  workspaceState = {},
  overrides = [],
  requiredNarratives = null
} = {}) => {
  const gates = [];

  // Only consider narratives that carry text; when requiredNarratives is passed,
  // restrict the checks to those triggers.
  const scoped = requiredNarratives
    ? narratives.filter((narrative) => requiredNarratives.includes(narrative.trigger))
    : narratives;
  const withText = scoped.filter((narrative) => String(narrative.text || '').trim());

  // Gate 1: exact-duplicate narrative text across two or more required narratives.
  const seen = new Map();
  let duplicateTriggers = null;
  withText.forEach((narrative) => {
    const key = normalizeForDuplicate(narrative.text);
    if (seen.has(key)) {
      duplicateTriggers = [seen.get(key), narrative.trigger];
    } else {
      seen.set(key, narrative.trigger);
    }
  });
  if (duplicateTriggers) {
    gates.push({
      code: 'duplicate_narrative',
      severity: 'error',
      blocking: true,
      message: 'Two required narratives are identical — each explanation must be distinct.',
      triggers: duplicateTriggers
    });
  }

  // Gate 2: narratives under the configured minimum word count.
  withText
    .filter((narrative) => wordCount(narrative.text) < MIN_NARRATIVE_WORDS)
    .forEach((narrative) => {
      gates.push({
        code: 'short_narrative',
        severity: 'error',
        blocking: true,
        message: `Commentary must be at least ${MIN_NARRATIVE_WORDS} words.`,
        triggers: [narrative.trigger]
      });
    });

  // Gate 3: baseline flagged — blocks unless a matching override is captured.
  if (workspaceState.baselineDecision === 'flagged') {
    const overridden = hasOverride(overrides, 'baseline_flagged');
    const override = (overrides || []).find((entry) => entry && entry.gateCode === 'baseline_flagged');
    gates.push({
      code: 'baseline_flagged',
      severity: 'warning',
      blocking: !overridden,
      message: workspaceState.baselineIssue
        ? `Baseline was flagged: ${workspaceState.baselineIssue}`
        : 'Baseline was flagged as having a data issue.',
      overrideOwner: override?.owner || null,
      overrideNote: override?.note || null
    });
  }

  // Gate 4: context warnings — each blocks unless overridden.
  (workspaceState.contextWarnings || []).forEach((warning, index) => {
    // Default MUST match the server evaluator's lookup key ('context_warning',
    // forecast_quality_gates.py) — an indexed code would let the UI show
    // 'Overridden' while the server still 422s the submit.
    const gateCode = warning?.gateCode || 'context_warning';
    const overridden = hasOverride(overrides, gateCode);
    const override = (overrides || []).find((entry) => entry && entry.gateCode === gateCode);
    gates.push({
      code: gateCode,
      severity: 'warning',
      blocking: !overridden,
      message: warning?.message || 'Planning-context warning requires acknowledgement.',
      overrideOwner: override?.owner || null,
      overrideNote: override?.note || null
    });
  });

  const status = gates.some((gate) => gate.blocking) ? 'blocked' : 'ok';
  return { status, gates };
};

export default evaluateQualityGates;
